/**
 * UnitDetailPage Component
 * Shows unit details organized into tabs: Overview, Leases, Documents, Expenses.
 * Mobile-first: prioritized information hierarchy.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Pencil, Trash2, UserPlus, Send, Eye, MoreHorizontal, MessageSquare, ChevronRight, ClipboardCheck,
} from 'lucide-react';
import type { MetadataSchemaField } from '../lib/api';
import { CounterpartyAddressesPanel } from '../components/CounterpartyAddressesPanel';
import {
  getUnit,
  updateUnit,
  deleteUnit,
  deleteProperty,
  createTenant,
  updateTenant,
  removeTenant,
  sendTenantInvite,
  resendTenantInvite,
  listLeases,
  createLease,
  updateLease,
  archiveLease,
  unarchiveLease,
  expensesApi,
  getLeaseWizardDraft,
  listUnitDisclosures,
  getUnitDisclosure,
  upsertUnitDisclosure,
  markDisclosureReviewed,
  listConversations,
  getConditionTemplate,
  ApiClientError,
  type UnitWithRelations,
  type TenantWithDetails,
  type CreateTenantData,
  type UpdateTenantData,
  type UnitDisclosureListItem,
  type UnitRoomResponse,
} from '../lib/api';
import { DisclosureFieldsForm } from '../components/lease-wizard/DisclosureFieldsForm';
import { MarketingTab } from '../components/unit-marketing/MarketingTab';
import type { LeaseDocumentDetailResponse } from '../types/lease-generation';
import { ensureWorkflow } from '../lib/workflow.api';
import { useWorkflowPanel } from '../components/workflow-panel/useWorkflowPanel';
import { hasActiveLeaseSetupSaga as computeHasActiveLeaseSetupSaga } from '../types/workflow';
import type {
  LeaseListItem,
  CreateLeaseInput,
  UpdateLeaseInput,
  ArchiveLeaseInput,
  LeaseValidationWarning,
} from '../types/lease';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LeaseForm } from '../components/LeaseForm';
import { LeaseCard } from '../components/LeaseCard';
import { ArchiveLeaseModal } from '../components/ArchiveLeaseModal';
import { RulesSection } from '../components/rules/RulesSection';
import { DocumentsSection } from '../components/documents/DocumentsSection';
import { ExpensesSection } from '../components/expenses/ExpensesSection';
import { PageHeader, PageSection, DataGrid, DataRow, StatusBadge, EmptyState } from '../components/layout/index';
import { EditableDataRow } from '../components/EditableDataRow';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { DetailTabs, TabsContent } from '../components/layout';
import { DisclosureCard } from '../components/DisclosureCard';
import { getDisclosureStatus } from '../components/disclosure-utils';
import { formatLocalDate, formatDateTime, deriveTimezone } from '../utils/date';
import { joinSelection, splitSelection } from '../utils/multiselect';
import { formatUnitLabel } from '@resida/shared';

/* ── Helpers ── */

/** Format a metadata field's current value for display in the DataGrid */
function formatMetadataValue(field: MetadataSchemaField): string | null {
  if (field.currentValue == null) return null;
  if (field.display.inputType === 'checkbox' || field.display.inputType === 'radio') return field.currentValue ? 'Yes' : 'No';
  if (field.display.inputType === 'select' && field.display.labels) {
    return field.display.labels[String(field.currentValue)] ?? String(field.currentValue);
  }
  return String(field.currentValue);
}

/* ── Inline Disclosure Card (wraps shared DisclosureCard with expand-to-edit) ── */

interface InlineDisclosureCardProps {
  item: UnitDisclosureListItem;
  unitId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
  variant?: 'required' | 'optional';
}

function InlineDisclosureCard({ item, unitId, isExpanded, onToggle, onSaved, variant = 'required' }: InlineDisclosureCardProps) {
  const status = getDisclosureStatus(item);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isExpanded) return;
    let cancelled = false;
    getUnitDisclosure(unitId, item.disclosureId)
      .then(res => { if (!cancelled) setFormData((res.disclosure?.data as Record<string, unknown>) ?? {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isExpanded, unitId, item.disclosureId]);

  const handleSave = async (complete: boolean) => {
    try {
      setIsSaving(true);
      setSaveError(null);
      await upsertUnitDisclosure(unitId, item.disclosureId, formData, complete);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkReviewed = async () => {
    try {
      setIsMarkingReviewed(true);
      setSaveError(null);
      await markDisclosureReviewed(unitId, item.disclosureId);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to mark as reviewed');
    } finally {
      setIsMarkingReviewed(false);
    }
  };

  const busy = isSaving || isMarkingReviewed;

  return (
    <div className={`rounded-lg border ${isExpanded ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border'}`}>
      <DisclosureCard
        item={item}
        className="" /* no extra border — outer div handles it */
      >
        {status !== 'Included' && (
          <Button
            size="sm"
            variant={isExpanded ? 'outline' : variant === 'optional' ? 'outline' : 'default'}
            className="shrink-0"
            onClick={onToggle}
            data-testid={`edit-disclosure-${item.disclosureId}`}
          >
            {isExpanded ? 'Collapse' : status === 'Missing' ? 'Add' : 'Edit'}
          </Button>
        )}
      </DisclosureCard>

      {isExpanded && status !== 'Included' && (
        <div className="border-t border-border px-4 py-4">
          <DisclosureFieldsForm
            disclosureId={item.disclosureId}
            unitId={unitId}
            value={formData}
            onChange={setFormData}
            disabled={busy}
          />

          {saveError && (
            <div className="mt-3 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-xs text-destructive">{saveError}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handleSave(false)} data-testid={`disclosure-save-draft-${item.disclosureId}`}>
              {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => handleSave(true)} data-testid={`disclosure-save-complete-${item.disclosureId}`}>
              {isSaving ? 'Saving...' : 'Save & Complete'}
            </Button>
            {item.record && (
              <Button size="sm" variant="outline" disabled={busy} onClick={handleMarkReviewed} data-testid={`disclosure-mark-reviewed-${item.disclosureId}`}>
                {isMarkingReviewed ? 'Marking...' : 'Mark as Reviewed'}
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={onToggle}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */

interface TenantFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export function UnitDetailPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workflows } = useWorkflowPanel();
  const queryClient = useQueryClient();

  const [unit, setUnit] = useState<UnitWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingReviewCount, setPendingReviewCount] = useState<number | null>(null);
  const [disclosures, setDisclosures] = useState<UnitDisclosureListItem[]>([]);
  const [expandedDisclosureId, setExpandedDisclosureId] = useState<string | null>(null);
  const incompleteDisclosureCount = disclosures.filter(d => d.required && d.hasFields && (!d.record || d.record.status !== 'COMPLETE')).length;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tenant management state
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [tenantForm, setTenantForm] = useState<TenantFormData | null>(null);
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [isSavingTenant, setIsSavingTenant] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);

  const [deletingTenantId, setDeletingTenantId] = useState<string | null>(null);
  const [isDeletingTenant, setIsDeletingTenant] = useState(false);

  const [sendingInviteTenantId, setSendingInviteTenantId] = useState<string | null>(null);
  const [viewingInviteTenantId, setViewingInviteTenantId] = useState<string | null>(null);

  // Lease management state
  const [leases, setLeases] = useState<LeaseListItem[]>([]);
  const [isLoadingLeases, setIsLoadingLeases] = useState(false);
  const [leaseError, setLeaseError] = useState<string | null>(null);
  const [leaseSubmitError, setLeaseSubmitError] = useState<string | null>(null);

  const [isLeaseFormOpen, setIsLeaseFormOpen] = useState(false);
  const [editingLeaseId] = useState<string | null>(null);
  const [editingLease] = useState<LeaseListItem | null>(null);
  const [isSavingLease, setIsSavingLease] = useState(false);
  const [leaseWarnings, setLeaseWarnings] = useState<LeaseValidationWarning[]>([]);
  // Bring-Your-Own form seed values, pre-filled from move-in suggestions (dollar amounts).
  const [leaseFormInitial, setLeaseFormInitial] = useState<{
    startDate?: string;
    rentAmount?: number;
    securityDeposit?: number;
    tenantIds?: string[];
  } | undefined>(undefined);

  const [isLeaseChoiceOpen, setIsLeaseChoiceOpen] = useState(false);
  const [leaseDraft, setLeaseDraft] = useState<LeaseDocumentDetailResponse | null>(null);
  const [isScheduledGuardOpen, setIsScheduledGuardOpen] = useState(false);

  const [archivingLease, setArchivingLease] = useState<LeaseListItem | null>(null);
  const [isArchivingLease, setIsArchivingLease] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Unit -> Chat link (TG-14): a lightweight pointer to the unit's newest conversation, if
  // any, so the "Message this unit's tenants" / "Open in Chat" link can either deep-link to
  // it or hand off to Chat's unified New Conversation flow, pre-scoped to this unit.
  const [unitConversationId, setUnitConversationId] = useState<string | null>(null);
  const [isLoadingUnitConversation, setIsLoadingUnitConversation] = useState(true);

  // Condition template rooms (BR-21) — null while unloaded/unknown (failed fetch included,
  // so a transient error never falsely triggers the "set up template" alert below); an
  // empty array means the template genuinely has zero non-deleted rooms.
  const [conditionRooms, setConditionRooms] = useState<UnitRoomResponse[] | null>(null);

  /* ── Data Fetching ── */

  const fetchData = useCallback(async () => {
    if (!unitId) return;
    try {
      setIsLoading(true);
      setError(null);
      const [unitRes, disclosureRes] = await Promise.all([
        getUnit(unitId),
        listUnitDisclosures(unitId).catch(() => ({ disclosures: [] as UnitDisclosureListItem[] })),
      ]);
      setUnit(unitRes.unit);
      setDisclosures(disclosureRes.disclosures);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unit');
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  const fetchLeases = useCallback(async () => {
    if (!unitId) return;
    try {
      setIsLoadingLeases(true);
      setLeaseError(null);
      const response = await listLeases({ unitId, state: 'all' });
      setLeases(response.leases || []);
    } catch (err) {
      setLeaseError(err instanceof Error ? err.message : 'Failed to load leases');
      setLeases([]);
    } finally {
      setIsLoadingLeases(false);
    }
  }, [unitId]);

  useEffect(() => {
    fetchData();
    fetchLeases();
    if (unitId) {
      getLeaseWizardDraft(unitId).then(setLeaseDraft).catch(() => setLeaseDraft(null));
    }
  }, [fetchData, fetchLeases, unitId]);

  useEffect(() => {
    if (!unitId) return;
    expensesApi.getReviewQueue()
      .then((res) => {
        const count = res.expenses.filter(
          (e) => e.aiUnitIdSuggested === unitId
        ).length;
        setPendingReviewCount(count);
      })
      .catch(() => setPendingReviewCount(null));
  }, [unitId]);

  /* ── Unit -> Chat Link ── */

  useEffect(() => {
    if (!unitId) return;
    let cancelled = false;
    setIsLoadingUnitConversation(true);
    listConversations({ unitId })
      .then(({ conversations }) => {
        if (cancelled) return;
        // If more than one conversation exists for the unit (e.g. a roster fork, TG-4),
        // link to the newest — same "most relevant" convention used elsewhere on this page.
        const newest =
          [...conversations].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0] ?? null;
        setUnitConversationId(newest?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setUnitConversationId(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUnitConversation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  /* ── Condition Template Rooms (BR-21) ── */

  useEffect(() => {
    if (!unitId) return;
    let cancelled = false;
    getConditionTemplate(unitId)
      .then(({ rooms }) => { if (!cancelled) setConditionRooms(rooms); })
      .catch(() => { if (!cancelled) setConditionRooms(null); });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  /* ── Unit Edit/Delete Handlers ── */

  const handleDelete = async () => {
    if (!unitId || !unit) return;
    const isSingleFamily = unit.property?.propertyType === 'single_family';
    try {
      setIsDeleting(true);
      if (isSingleFamily) {
        await deleteProperty(unit.propertyId);
        navigate('/properties');
      } else {
        await deleteUnit(unitId);
        navigate(`/properties/${unit.propertyId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to delete ${isSingleFamily ? 'property' : 'unit'}`);
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  /* ── Tenant Handlers ── */

  const openAddTenantModal = () => {
    setTenantForm({ firstName: '', lastName: '', email: '', phone: '' });
    setEditingTenantId(null);
    setTenantError(null);
    setIsTenantModalOpen(true);
  };

  const openEditTenantModal = (tenant: TenantWithDetails) => {
    setTenantForm({
      firstName: tenant.firstName || '',
      lastName: tenant.lastName || '',
      email: tenant.email,
      phone: tenant.phone || '',
    });
    setEditingTenantId(tenant.id);
    setTenantError(null);
    setIsTenantModalOpen(true);
  };

  const handleTenantFormChange = (field: keyof TenantFormData, value: string) => {
    if (tenantForm) {
      setTenantForm({ ...tenantForm, [field]: value });
    }
  };

  const handleSaveTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unitId || !tenantForm) return;
    try {
      setIsSavingTenant(true);
      setTenantError(null);
      if (editingTenantId) {
        const updateData: UpdateTenantData = {
          email: tenantForm.email.trim() || undefined,
          firstName: tenantForm.firstName.trim() || undefined,
          lastName: tenantForm.lastName.trim() || undefined,
          phone: tenantForm.phone.trim() || undefined,
        };
        await updateTenant(editingTenantId, updateData);
      } else {
        const createData: CreateTenantData = {
          email: tenantForm.email.trim(),
          firstName: tenantForm.firstName.trim() || undefined,
          lastName: tenantForm.lastName.trim() || undefined,
          phone: tenantForm.phone.trim() || undefined,
        };
        await createTenant(unitId, createData);
      }
      await fetchData();
      setIsTenantModalOpen(false);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Failed to save tenant');
    } finally {
      setIsSavingTenant(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!deletingTenantId) return;
    try {
      setIsDeletingTenant(true);
      await removeTenant(deletingTenantId);
      await fetchData();
      setDeletingTenantId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove tenant');
      setDeletingTenantId(null);
    } finally {
      setIsDeletingTenant(false);
    }
  };

  const handleSendInvite = async (tenantId: string, isResend: boolean = false) => {
    if (!unitId) return;
    try {
      setSendingInviteTenantId(tenantId);
      if (isResend) {
        await resendTenantInvite(tenantId);
      } else {
        await sendTenantInvite(unitId, tenantId);
      }
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isResend ? 'resend' : 'send'} invite`);
    } finally {
      setSendingInviteTenantId(null);
    }
  };

  /* ── Lease Handlers ── */

  const openCreateLeaseWizard = () => {
    navigate(`/units/${unitId}/create-lease`);
  };

  const handleCreateLeaseClick = () => {
    const hasScheduled = leases.some(l => l.state === 'scheduled');
    if (hasScheduled) {
      setIsScheduledGuardOpen(true);
      return;
    }
    setIsLeaseChoiceOpen(true);
  };

  const openManualLeaseForm = async () => {
    setIsLeaseChoiceOpen(false);
    setLeaseWarnings([]);
    setLeaseSubmitError(null);
    // Pre-fill the Bring-Your-Own form from move-in suggestions (rent/deposit/start date in
    // dollars) and pre-select the unit's tenants, so the landlord isn't re-typing known data.
    let initial: typeof leaseFormInitial = unitId
      ? { tenantIds: (unit?.tenants ?? []).map((t) => t.id) }
      : undefined;
    if (unitId) {
      try {
        const { getMoveIn } = await import('../lib/move-in.api');
        const proj = await getMoveIn(unitId);
        const valueFor = (field: string): number | string | undefined => {
          const s = proj.suggestions.find((x) => x.field === field && x.preference === null);
          return s ? (s.proposedValue as number | string) : undefined;
        };
        const rent = valueFor('inputs.rentAmount');
        const deposit = valueFor('inputs.securityDeposit');
        const start = valueFor('inputs.startDate');
        initial = {
          ...initial,
          ...(typeof rent === 'number' ? { rentAmount: rent } : {}),
          ...(typeof deposit === 'number' ? { securityDeposit: deposit } : {}),
          ...(typeof start === 'string' ? { startDate: start.substring(0, 10) } : {}),
        };
      } catch {
        /* Non-fatal — open the form without move-in pre-fill */
      }
    }
    setLeaseFormInitial(initial);
    setIsLeaseFormOpen(true);
  };

  const handleSaveLease = async (
    data: CreateLeaseInput | UpdateLeaseInput,
    acknowledgeWarnings: boolean
  ) => {
    if (!unitId) return;
    try {
      setIsSavingLease(true);
      setLeaseSubmitError(null);
      setLeaseWarnings([]);

      if (editingLeaseId) {
        const response = await updateLease(unitId, editingLeaseId, data as UpdateLeaseInput);
        if (response.warnings && response.warnings.length > 0 && !acknowledgeWarnings) {
          setLeaseWarnings(response.warnings);
          setIsSavingLease(false);
          return;
        }
      } else {
        const response = await createLease(unitId, data as CreateLeaseInput);
        if (response.warnings && response.warnings.length > 0 && !acknowledgeWarnings) {
          setLeaseWarnings(response.warnings);
          setIsSavingLease(false);
          return;
        }
      }

      await fetchLeases();
      await fetchData();
      setIsLeaseFormOpen(false);
      setLeaseWarnings([]);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 422) {
        if (err.details && typeof err.details === 'object' && 'validationResult' in err.details) {
          const validationResult = (err.details as { validationResult?: { warnings?: Array<{ code: string; message: string }> } }).validationResult;
          if (validationResult?.warnings && Array.isArray(validationResult.warnings)) {
            const warnings = validationResult.warnings.map((w: LeaseValidationWarning) => ({
              code: w.code,
              message: w.message,
            }));
            setLeaseWarnings(warnings);
            setIsSavingLease(false);
            return;
          }
        }
      }
      setLeaseSubmitError(err instanceof Error ? err.message : 'Failed to save lease');
    } finally {
      setIsSavingLease(false);
    }
  };

  const openArchiveLeaseModal = (lease: LeaseListItem) => {
    setArchivingLease(lease);
  };

  const handleArchiveLease = async (data: ArchiveLeaseInput) => {
    if (!unitId || !archivingLease) return;
    try {
      setIsArchivingLease(true);
      setArchiveError(null);
      await archiveLease(unitId, archivingLease.id, data);
      await fetchLeases();
      await fetchData();
      setArchivingLease(null);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to archive lease');
    } finally {
      setIsArchivingLease(false);
    }
  };

  const handleUnarchiveLease = async (lease: LeaseListItem) => {
    try {
      await unarchiveLease(lease.id);
      await fetchLeases();
      await fetchData();
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to un-archive lease');
    }
  };

  // Auto-open the Details tab if edit=true query param is present
  useEffect(() => {
    if (unit && searchParams.get('edit') === 'true') {
      window.location.hash = 'details';
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, searchParams]);

  // Auto-open the Bring-Your-Own lease form if leaseForm=byo is present. The workflow panel's
  // create_lease → "Bring Your Own" action navigates here with this param; without this handler
  // the modal closed and nothing opened (the BYO form lives on this page, not in the panel).
  useEffect(() => {
    if (unit && searchParams.get('leaseForm') === 'byo' && !isLeaseFormOpen) {
      void openManualLeaseForm();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, searchParams]);

  // Auto-open the lease-creation entry (wizard-vs-BYO choice, or the scheduled-lease guard)
  // if createLease=true is present. The Leases page's "Create Lease" unit picker navigates
  // here with this param after the landlord selects a unit — same pattern as leaseForm=byo.
  // Gated on !isLoadingLeases (in addition to unit being loaded): handleCreateLeaseClick()
  // decides guard-vs-choice-modal by reading `leases` from closure, and `fetchData`/
  // `fetchLeases` are two independent, concurrently-kicked-off requests (mount effect above)
  // with nothing sequencing them. Without this gate, if getUnit() resolves before
  // listLeases() does, this effect could fire while `leases` is still its initial `[]`,
  // masking a real scheduled lease and skipping the guard (AC-3, severity: critical).
  useEffect(() => {
    if (unit && !isLoadingLeases && searchParams.get('createLease') === 'true' &&
        !isLeaseChoiceOpen && !isScheduledGuardOpen) {
      handleCreateLeaseClick();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, isLoadingLeases, searchParams]);

  // Convert ?tab=X query param to #X hash so DetailTabs can pick it up.
  // Uses navigate() with replace so the URL change re-triggers DetailTabs initialization.
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && unitId) {
      navigate(`/units/${unitId}#${tabParam}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Helpers ── */

  const getPendingInvite = (tenant: TenantWithDetails) => {
    return tenant.invites?.find(
      (invite) => invite.status === 'pending' && !invite.revokedAt && !invite.consumedAt
    );
  };

  const getInviteLink = (token: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/tenant/invite/claim?token=${token}`;
  };

  const getTenantStatus = (tenant: TenantWithDetails) => {
    const hasPending = tenant.invites?.some(i => i.status === 'pending' && !i.revokedAt && !i.consumedAt);
    const hasConsumed = tenant.invites?.some(i => i.status === 'consumed' && i.consumedAt);
    if (hasConsumed) return 'active' as const;
    if (hasPending) return 'invited' as const;
    return 'not-invited' as const;
  };

  /* ── Derived Data ── */

  const activeLeases = leases?.filter(l => l.state === 'active') || [];
  // 'ending' leases still occupy the unit — tenancy is not over until 'ended' (BR-LL-01/09) —
  // so they're grouped with the still-occupying leases, not with the wound-down ones.
  const endingLeases = leases?.filter(l => l.state === 'ending') || [];
  const scheduledLeases = leases?.filter(l => l.state === 'scheduled') || [];
  const endedLeases = leases?.filter(l => l.state === 'ended') || [];
  const archivedLeases = leases?.filter(l => l.state === 'archived') || [];
  const isSingleFamily = unit?.property?.propertyType === 'single_family';

  // Determine whether a lease_setup workflow is already tracked for this unit via the panel
  // (saga member or standalone atomic) — see hasActiveLeaseSetupSaga's doc comment for why this
  // checks membership, not member status (issue #715).
  const hasActiveLeaseSetupSaga = computeHasActiveLeaseSetupSaga(workflows, unitId);
  const unitTitle = unit
    ? (formatUnitLabel(unit.unitNumber) ?? unit.property?.name)
    : '';

  /* ── Loading / Error / Not Found ── */

  // Gated on `!unit` (not just `isLoading`) so that a background refetch after an inline
  // field save (EditableDataRow's onSave -> fetchData()) re-renders the already-mounted
  // page in place instead of swapping the whole tree for this skeleton — which was
  // unmounting/remounting the page on every Yes/No or Confirm click and resetting scroll
  // to the top.
  if (isLoading && !unit) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4 space-y-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="flex gap-2 mt-4">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
          </div>
          <Skeleton className="h-48 w-full mt-4" />
        </div>
      </div>
    );
  }

  if (error && !unit) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <EmptyState
          title="Error Loading Unit"
          description={error}
          action={
            <div className="flex gap-2">
              <Button onClick={fetchData}>Retry</Button>
              <Button variant="outline" onClick={() => navigate('/units')}>Back to Units</Button>
            </div>
          }
        />
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <EmptyState
          title="Unit Not Found"
          action={<Button variant="outline" onClick={() => navigate('/units')}>Back to Units</Button>}
        />
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring box-border";
  const labelClass = "block mb-1 text-sm font-medium text-foreground";

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);


  return (
    <div className="max-w-4xl mx-auto pb-6">
      {/* ── Header ── */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title={unitTitle || 'Unit'}
          subtitleNode={
            <span data-testid="unit-address-subtitle">
              {`${unit.address1}${unit.address2 ? `, ${unit.address2}` : ''}, ${unit.city}, ${unit.state} ${unit.zipCode}`}
            </span>
          }
          backLink={{
            label: isSingleFamily ? 'Properties' : (unit.property?.name ?? 'Property'),
            to: isSingleFamily ? '/properties' : `/properties/${unit.propertyId}`,
          }}
          action={
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => { window.location.hash = 'details'; }}>
                <Pencil className="h-3.5 w-3.5 md:mr-1" />
                <span className="hidden md:inline">Edit</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsDeleteDialogOpen(true)} className="text-destructive hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5 md:mr-1" />
                <span className="hidden md:inline">Delete</span>
              </Button>
            </div>
          }
        />
      </div>

      {/* Status + Specs */}
      <div className="px-[var(--page-padding-x)] mb-2 flex items-center gap-2">
        <StatusBadge status={unit.occupancy === 'available' ? 'vacant' : 'occupied'} />
        {unit.property && !isSingleFamily && (
          <Link to={`/properties/${unit.propertyId}`} className="text-xs text-primary hover:underline">
            {unit.property.name}
          </Link>
        )}
      </div>

      {(() => {
        const metaFields = unit.metadataSchema?.fields?.filter(f => f.required || f.recommended) ?? [];
        const filledMeta = metaFields.filter(f => f.currentValue != null);
        const missingRequired = metaFields.filter(f => f.required && f.currentValue == null);
        const missingRecommended = metaFields.filter(f => f.recommended && !f.required && f.currentValue == null);
        const hasStats = unit.bedrooms != null || unit.bathrooms != null || unit.sqft || filledMeta.length > 0;
        return (
          <>
            {hasStats && (
              <div className="px-[var(--page-padding-x)] mb-4" data-testid="unit-metadata-section">
                <DataGrid columns={3}>
                  {unit.bedrooms != null && <DataRow label="Bedrooms" value={unit.bedrooms} data-testid="bedrooms-row" />}
                  {unit.bathrooms != null && <DataRow label="Bathrooms" value={unit.bathrooms} data-testid="bathrooms-row" />}
                  {unit.sqft && <DataRow label="Sq Ft" value={unit.sqft.toLocaleString()} />}
                  {filledMeta.map(f => (
                    <DataRow key={f.name} label={f.label} value={formatMetadataValue(f)} data-testid={`metadata-field-${f.name}`} />
                  ))}
                </DataGrid>
              </div>
            )}
            {missingRequired.length > 0 && (
              <div
                data-testid="metadata-missing-required-warning"
                className="px-[var(--page-padding-x)] mb-4"
              >
                <div className="flex items-start gap-3 px-4 py-3 bg-warning/10 border border-warning/30 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Complete your unit details</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {unit.state} requires {missingRequired.map(f => f.label.toLowerCase()).join(', ')} to determine which disclosures apply to this unit.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { window.location.hash = 'details'; }}
                    data-testid="metadata-edit-button"
                    className="shrink-0"
                  >
                    Add details
                  </Button>
                </div>
              </div>
            )}
            {missingRecommended.length > 0 && missingRequired.length === 0 && (
              <div
                data-testid="metadata-missing-recommended-warning"
                className="px-[var(--page-padding-x)] mb-4"
              >
                <div className="flex items-start gap-3 px-4 py-3 bg-muted/50 border border-border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Recommended details missing</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Adding {missingRecommended.map(f => f.label.toLowerCase()).join(', ')} helps determine which disclosures apply to this unit.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { window.location.hash = 'details'; }}
                    data-testid="metadata-edit-recommended-button"
                    className="shrink-0"
                  >
                    Add details
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Condition template incomplete-setup alert (BR-21/US-17): rendered here — above
          the tabs, alongside "Complete your unit details" — rather than inside the
          Condition tab's own content, so a landlord sees it immediately on arrival
          (default tab is Overview) instead of only after already navigating into
          Condition. Mirrors the unit-details alert's placement and visibility exactly. */}
      {conditionRooms !== null && conditionRooms.length === 0 && (
        <div
          data-testid="condition-template-incomplete-warning"
          className="px-[var(--page-padding-x)] mb-4"
        >
          <div className="flex items-start gap-3 px-4 py-3 bg-warning/10 border border-warning/30 rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Set up your condition template</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                This unit doesn&apos;t have any rooms in its condition template yet. Add rooms and
                items to enable move-in/move-out condition walkthroughs.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/units/${unitId}/condition-template`)}
              data-testid="condition-template-setup-button"
              className="shrink-0"
            >
              Set up template
            </Button>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <DetailTabs
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'condition', label: 'Condition', testId: 'condition-tab' },
          { key: 'leases', label: 'Leases', badge: leases.length || undefined },
          { key: 'disclosures', label: 'Disclosures', testId: 'disclosures-tab', badge: incompleteDisclosureCount || undefined },
          { key: 'documents', label: 'Documents', testId: 'documents-tab' },
          { key: 'expenses', label: 'Expenses', badge: pendingReviewCount ?? undefined },
          { key: 'marketing', label: 'Marketing', testId: 'marketing-tab' },
          { key: 'details', label: 'Details', testId: 'details-tab' },
        ]}
        defaultTab="overview"
      >

        {/* ── Overview Tab ── */}
        <TabsContent value="overview" className="mt-0">
          {/* Tenants */}
          <PageSection
            title="Tenants"
            badge={unit.tenants?.length || undefined}
            action={
              <Button variant="ghost" size="sm" onClick={openAddTenantModal} data-testid="add-tenant-button">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            }
          >
            {unit.tenants && unit.tenants.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="tenant-list">
                {unit.tenants.map((tenant) => {
                  const status = getTenantStatus(tenant);
                  const hasPendingInvite = status === 'invited';
                  const hasConsumedInvite = status === 'active';
                  const name = tenant.firstName && tenant.lastName
                    ? `${tenant.firstName} ${tenant.lastName}`
                    : tenant.email;

                  return (
                    <div
                      key={tenant.id}
                      className="flex flex-col gap-2 p-3 rounded-lg border border-border"
                      data-testid="tenant-list-item"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">{name}</span>
                            <StatusBadge status={status} size="sm" />
                          </div>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                            {tenant.email && <span>{tenant.email}</span>}
                            {tenant.phone && <span>{tenant.phone}</span>}
                          </div>
                        </div>
                      </div>
                      {/* Actions row — stacks on mobile */}
                      <div className="flex gap-1.5 flex-wrap">
                        <Button variant="ghost" size="sm" onClick={() => openEditTenantModal(tenant)} data-testid="edit-tenant-button">
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        {!hasConsumedInvite && (
                          <>
                            {!hasPendingInvite ? (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleSendInvite(tenant.id, false)}
                                disabled={sendingInviteTenantId === tenant.id}
                                data-testid="send-invite-button"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                {sendingInviteTenantId === tenant.id ? 'Sending...' : 'Invite'}
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleSendInvite(tenant.id, true)}
                                  disabled={sendingInviteTenantId === tenant.id}
                                  data-testid="resend-invite-button"
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  {sendingInviteTenantId === tenant.id ? 'Sending...' : 'Resend'}
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setViewingInviteTenantId(tenant.id)}
                                  data-testid="view-invite-link-button"
                                >
                                  <Eye className="h-3 w-3 mr-1" /> Link
                                </Button>
                              </>
                            )}
                          </>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeletingTenantId(tenant.id)}
                          data-testid="delete-tenant-button"
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="No tenants added yet"
                description="Add a tenant to manage occupancy and send invites."
                action={<Button size="sm" onClick={openAddTenantModal}><UserPlus className="h-3.5 w-3.5 mr-1" /> Add Tenant</Button>}
              />
            )}
          </PageSection>

          {/* Messages (TG-14): a lightweight link into Chat — no message thread or
              Telegram-specific action lives on this page anymore. */}
          <PageSection title="Messages" data-testid="unit-chat-section">
            {isLoadingUnitConversation ? (
              <Skeleton className="h-14 w-full rounded-lg" />
            ) : (
              <Link
                to={
                  unitConversationId
                    ? `/chat?conversationId=${unitConversationId}`
                    : `/chat?newConversationUnitId=${unitId}`
                }
                className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors no-underline min-h-[44px]"
                data-testid="unit-chat-link"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">
                    {unitConversationId ? 'Open in Chat' : "Message this unit's tenants"}
                  </span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </Link>
            )}
          </PageSection>

          {/* Draft In Progress Banner */}
          {leaseDraft && (
            <div
              data-testid="lease-draft-banner"
              className="flex items-center justify-between gap-3 p-3 rounded-lg border border-warning/40 bg-warning/5"
            >
              <div>
                <p className="text-sm font-medium text-foreground">Lease draft in progress</p>
                <p className="text-xs text-muted-foreground">
                  Step {(leaseDraft.variables?.wizardCurrentStep as number | undefined) ?? 1} of 9 — saved {formatDateTime(leaseDraft.updatedAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <Button
                size="sm"
                data-testid="resume-draft-button"
                onClick={() => navigate(`/units/${unitId}/create-lease`)}
                className="shrink-0"
              >
                Resume Draft
              </Button>
            </div>
          )}

          {/* Active & Scheduled Lease Summary */}
          <PageSection
            title="Active & Scheduled"
            action={
              <Button
                variant="ghost" size="sm"
                onClick={handleCreateLeaseClick}
                data-testid="create-lease-button"
              >
                Create Lease
              </Button>
            }
          >
            {(activeLeases.length > 0 || scheduledLeases.length > 0 || endingLeases.length > 0) ? (
              [...activeLeases.map((lease) => ({ lease, status: 'active' as const })),
               ...endingLeases.map((lease) => ({ lease, status: 'ending' as const })),
               ...scheduledLeases.map((lease) => ({ lease, status: 'scheduled' as const }))]
                .map(({ lease, status }) => (
                  <Link
                    key={lease.id}
                    to={`/leases/${lease.id}`}
                    className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors no-underline"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-primary tabular-nums">
                          {formatCurrency(lease.rentAmount / 100)}/mo
                        </span>
                        <StatusBadge status={status} size="sm" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatLocalDate(lease.startDate)} — {formatLocalDate(lease.endDate)}
                      </p>
                      {lease.tenants.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {lease.tenants.map(t => `${t.firstName} ${t.lastName}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
                  </Link>
                ))
            ) : (
              <EmptyState
                title="No active lease"
                description="Create a lease to start collecting rent."
                action={
                  <div className="flex flex-col gap-2 items-center">
                    <Button size="sm" onClick={handleCreateLeaseClick} data-testid="create-lease-button-empty">
                      Create Lease
                    </Button>
                    {!hasActiveLeaseSetupSaga && unitId && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="setup-lease-workflow-btn"
                        aria-label="Start guided lease setup workflow"
                        onClick={() => {
                          void ensureWorkflow({
                            type: 'lease_setup',
                            dedupeKey: `lease_setup:unit:${unitId}`,
                            lineageType: 'unit',
                            lineageId: unitId,
                            subjectType: 'unit',
                            subjectId: unitId,
                            triggerSource: 'unit_lease_list_cta',
                          }).then(() => {
                            void queryClient.invalidateQueries({ queryKey: ['workflows'] });
                          });
                        }}
                      >
                        Set up lease (guided)
                      </Button>
                    )}
                  </div>
                }
              />
            )}
          </PageSection>
        </TabsContent>

        {/* ── Condition Tab (BR-21) ── */}
        <TabsContent value="condition" className="mt-0">
          {/* Condition Checklist (JR-1): entry point into the per-unit room/item
              template editor used to set up move-in/move-out walkthroughs. */}
          <PageSection title="Condition Checklist" data-testid="unit-condition-template-section">
            <Link
              to={`/units/${unitId}/condition-template`}
              className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors no-underline min-h-[44px]"
              data-testid="unit-condition-template-link"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium text-foreground truncate">
                  Manage Condition Template
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          </PageSection>
        </TabsContent>

        {/* ── Leases Tab ── */}
        <TabsContent value="leases" className="mt-0">
          <PageSection
            title="All Leases"
            badge={leases.length || undefined}
            action={
              <Button variant="ghost" size="sm" onClick={handleCreateLeaseClick}>
                Create Lease
              </Button>
            }
          >
            {leaseError && (
              <div className="p-3 mb-3 rounded-md border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                {leaseError}
              </div>
            )}
            {isLoadingLeases ? (
              <div className="space-y-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : leases.length === 0 ? (
              <EmptyState
                title="No leases"
                description="Create a lease to manage tenancy."
              />
            ) : (
              <div className="flex flex-col gap-3">
                {activeLeases.length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active</h4>
                    {activeLeases.map((lease) => (
                      <LeaseCard key={lease.id} lease={lease} onArchive={openArchiveLeaseModal} />
                    ))}
                  </>
                )}
                {endingLeases.length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2">Ending</h4>
                    {endingLeases.map((lease) => (
                      <LeaseCard key={lease.id} lease={lease} onArchive={openArchiveLeaseModal} />
                    ))}
                  </>
                )}
                {scheduledLeases.length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2">Scheduled</h4>
                    {scheduledLeases.map((lease) => (
                      <LeaseCard key={lease.id} lease={lease} onArchive={openArchiveLeaseModal} />
                    ))}
                  </>
                )}
                {endedLeases.length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2">Ended</h4>
                    {endedLeases.map((lease) => (
                      <LeaseCard key={lease.id} lease={lease} onArchive={openArchiveLeaseModal} />
                    ))}
                  </>
                )}
                {archivedLeases.length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2">Archived</h4>
                    {archivedLeases.map((lease) => (
                      <LeaseCard key={lease.id} lease={lease} onUnarchive={handleUnarchiveLease} />
                    ))}
                  </>
                )}
              </div>
            )}
          </PageSection>
        </TabsContent>

        {/* ── Disclosures Tab ── */}
        <TabsContent value="disclosures" className="mt-0">
          {unitId && (() => {
            const requiredDisclosures = disclosures.filter(d => d.required);
            const optionalDisclosures = disclosures.filter(d => !d.required);
            const allRequiredComplete = requiredDisclosures.every(d => !d.hasFields || d.record?.status === 'COMPLETE');
            const anyStale = disclosures.some(d => d.isStale);
            const missingCount = requiredDisclosures.filter(d => d.hasFields && !d.record).length;
            const draftCount = requiredDisclosures.filter(d => d.hasFields && d.record?.status === 'DRAFT').length;

            return (
              <div className="px-[var(--page-padding-x)] py-4" data-testid="disclosures-tab-content">
                {/* Readiness Banner */}
                {disclosures.length > 0 && (allRequiredComplete && !anyStale ? (
                  <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-success/10 border border-success/20 rounded-lg" data-testid="disclosures-ready-banner" role="status">
                    <svg className="w-5 h-5 text-success shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-success">All disclosures complete — ready for lease generation.</span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 px-4 py-3 mb-4 bg-warning/10 border border-warning/20 rounded-lg" data-testid="disclosures-incomplete-banner" role="status">
                    <span className="text-sm font-medium text-warning">
                      {missingCount > 0 && `${missingCount} disclosure${missingCount !== 1 ? 's' : ''} missing. `}
                      {draftCount > 0 && `${draftCount} in draft. `}
                      {anyStale && 'Some may be stale. '}
                      Complete all required disclosures before generating a lease.
                    </span>
                  </div>
                ))}

                {/* Required Disclosures */}
                {requiredDisclosures.length > 0 && (
                  <section className="mb-6">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Required by Jurisdiction</h3>
                    <div className="flex flex-col gap-3" data-testid="required-disclosures-list">
                      {requiredDisclosures.map(item => (
                        <InlineDisclosureCard
                          key={item.disclosureId}
                          item={item}
                          unitId={unitId!}
                          isExpanded={expandedDisclosureId === item.disclosureId}
                          onToggle={() => setExpandedDisclosureId(prev => prev === item.disclosureId ? null : item.disclosureId)}
                          onSaved={() => { setExpandedDisclosureId(null); fetchData(); }}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* Optional Disclosures */}
                {optionalDisclosures.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Optional Disclosures</h3>
                    <div className="flex flex-col gap-3" data-testid="optional-disclosures-list">
                      {optionalDisclosures.map(item => (
                        <InlineDisclosureCard
                          key={item.disclosureId}
                          item={item}
                          unitId={unitId!}
                          isExpanded={expandedDisclosureId === item.disclosureId}
                          onToggle={() => setExpandedDisclosureId(prev => prev === item.disclosureId ? null : item.disclosureId)}
                          onSaved={() => { setExpandedDisclosureId(null); fetchData(); }}
                          variant="optional"
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* Empty state */}
                {disclosures.length === 0 && (
                  <EmptyState
                    title="No disclosures required"
                    description="No disclosure requirements found for this unit's jurisdiction."
                  />
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* ── Documents Tab ── */}
        <TabsContent value="documents" className="mt-0">
          {unitId && (
            <>
              <RulesSection scope="UNIT" unitId={unitId} />
              <DocumentsSection scope="UNIT" unitId={unitId} />
            </>
          )}
        </TabsContent>

        {/* ── Expenses Tab ── */}
        <TabsContent value="expenses" className="mt-0">
          {unit && (
            <>
              {pendingReviewCount != null && pendingReviewCount > 0 && (
                <div
                  className="flex items-center gap-3 mx-[var(--page-padding-x)] mt-4 px-3 py-2 rounded-lg bg-warning/10 border border-warning/25"
                  data-testid="unit-pending-review-badge"
                >
                  <span className="text-sm font-medium text-warning">
                    {pendingReviewCount} expense{pendingReviewCount !== 1 ? 's' : ''} pending review
                  </span>
                  <Link
                    to="/expenses/confirm"
                    className="text-sm font-semibold text-warning no-underline hover:underline"
                  >
                    Review Queue &rarr;
                  </Link>
                </div>
              )}
              <ExpensesSection propertyId={unit.propertyId} unitId={unitId} />
            </>
          )}
        </TabsContent>

        {/* ── Marketing Tab ── */}
        <TabsContent value="marketing" className="mt-0">
          {unitId && <MarketingTab unitId={unitId} />}
        </TabsContent>

        {/* ── Details Tab (AC-D1-AC-D6) ── */}
        <TabsContent value="details" className="mt-0">
          <PageSection title="Address">
            <DataGrid columns={2}>
              <EditableDataRow
                label="Street Address"
                value={unit.address1}
                type="text"
                data-testid="details-address1"
                onSave={async (v) => {
                  await updateUnit(unitId!, { address1: v.trim() });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="Apt/Suite (Optional)"
                value={unit.address2 ?? ''}
                type="text"
                data-testid="details-address2"
                onSave={async (v) => {
                  await updateUnit(unitId!, { address2: v.trim() ? v.trim() : null });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="City"
                value={unit.city}
                type="text"
                data-testid="details-city"
                onSave={async (v) => {
                  await updateUnit(unitId!, { city: v.trim() });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="State"
                value={unit.state}
                type="text"
                data-testid="details-state"
                onSave={async (v) => {
                  await updateUnit(unitId!, { state: v.trim() });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="Zip"
                value={unit.zipCode}
                type="text"
                data-testid="details-zip"
                onSave={async (v) => {
                  await updateUnit(unitId!, { zipCode: v.trim() });
                  await fetchData();
                }}
              />
            </DataGrid>
          </PageSection>

          <PageSection title="Unit Facts">
            <DataGrid columns={3}>
              {/* Non-single-family units may be renamed/renumbered after creation (parity
                  with the retired "Edit Unit" modal's conditional Unit Number field, which
                  server-side unitService.update() rejects any value but the sentinel for
                  single_family units, mirroring BR-11's single_family guard). */}
              {!isSingleFamily && (
                <EditableDataRow
                  label="Unit Number"
                  value={unit.unitNumber ?? ''}
                  type="text"
                  data-testid="details-unit-number"
                  onSave={async (v) => {
                    await updateUnit(unitId!, { unitNumber: v.trim() });
                    await fetchData();
                  }}
                />
              )}
              <EditableDataRow
                label="Bedrooms"
                value={unit.bedrooms != null ? String(unit.bedrooms) : ''}
                type="number"
                data-testid="details-bedrooms"
                onSave={async (v) => {
                  await updateUnit(unitId!, { bedrooms: v ? parseInt(v, 10) : null });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="Bathrooms"
                value={unit.bathrooms ?? ''}
                type="number"
                data-testid="details-bathrooms"
                onSave={async (v) => {
                  await updateUnit(unitId!, { bathrooms: v || null });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="Square Feet"
                value={unit.sqft != null ? String(unit.sqft) : ''}
                type="number"
                data-testid="details-sqft"
                onSave={async (v) => {
                  await updateUnit(unitId!, { sqft: v ? parseInt(v, 10) : null });
                  await fetchData();
                }}
              />
              <EditableDataRow
                label="Year Built"
                value={unit.metadata?.constructionYear != null ? String(unit.metadata.constructionYear) : ''}
                type="number"
                data-testid="details-year-built"
                onSave={async (v) => {
                  await updateUnit(unitId!, {
                    metadata: { ...(unit.metadata ?? {}), constructionYear: v ? parseInt(v, 10) : undefined },
                  });
                  await fetchData();
                }}
              />
            </DataGrid>
          </PageSection>

          {(() => {
            const jurisdictionFields = unit.metadataSchema?.fields?.filter(f => f.required || f.recommended) ?? [];
            if (jurisdictionFields.length === 0) return null;
            const missingRequiredCount = jurisdictionFields.filter(f => f.required && f.currentValue == null).length;
            return (
              <PageSection
                title={`${unit.state} Requirements`}
                action={
                  missingRequiredCount > 0 ? (
                    <Badge
                      variant="warning"
                      data-testid="details-jurisdiction-unanswered-count"
                    >
                      {missingRequiredCount} unanswered
                    </Badge>
                  ) : undefined
                }
              >
                <p className="text-xs text-muted-foreground mb-4">
                  These details determine which disclosures apply to this unit.
                </p>
                <DataGrid columns={2}>
                  {jurisdictionFields.map((field) => {
                    const missing = field.required && field.currentValue == null;
                    const fieldType: 'text' | 'number' | 'select' | 'multiselect' | 'yesno' =
                      field.display.inputType === 'select'
                        ? 'select'
                        : field.display.inputType === 'multiselect'
                        ? 'multiselect'
                        : field.display.inputType === 'radio' || field.display.inputType === 'checkbox'
                        ? 'yesno'
                        : field.display.inputType === 'number'
                        ? 'number'
                        : 'text';
                    const options =
                      fieldType === 'select' || fieldType === 'multiselect'
                        ? field.validation.values?.map((v) => ({ value: v, label: field.display.labels?.[v] ?? v }))
                        : undefined;
                    // For 'select'/'multiselect', EditableDataRow needs the raw code(s) (to pre-fill the
                    // control correctly when editing starts) and resolves them to labels itself via
                    // `options`. Every other type displays fine as-is.
                    const rowValue =
                      fieldType === 'multiselect'
                        ? (Array.isArray(field.currentValue) ? joinSelection(field.currentValue.map(String)) : '')
                        : fieldType === 'select'
                        ? (field.currentValue != null ? String(field.currentValue) : '')
                        : formatMetadataValue(field) ?? '';
                    return (
                      <EditableDataRow
                        key={field.name}
                        label={field.label}
                        value={rowValue}
                        type={fieldType}
                        options={options}
                        warning={missing}
                        defaultExpanded={missing}
                        description={field.description}
                        data-testid={`details-metadata-field-${field.name}`}
                        onSave={async (newValue) => {
                          const parsed: unknown =
                            fieldType === 'yesno'
                              ? newValue === 'true'
                              : fieldType === 'multiselect'
                              ? splitSelection(newValue)
                              : field.type === 'number'
                              ? (newValue === '' ? null : Number(newValue))
                              : newValue;
                          await updateUnit(unitId!, { metadata: { ...(unit.metadata ?? {}), [field.name]: parsed } });
                          await fetchData();
                        }}
                      />
                    );
                  })}
                </DataGrid>
              </PageSection>
            );
          })()}
        </TabsContent>
      </DetailTabs>

      {/* ═══════════════════ MODALS ═══════════════════ */}

      {/* Add / Edit Tenant Modal */}
      <Modal isOpen={isTenantModalOpen} onClose={() => setIsTenantModalOpen(false)} title={editingTenantId ? 'Edit Tenant' : 'Add Tenant'} width="medium">
        {tenantForm && (
          <form onSubmit={handleSaveTenant}>
            {tenantError && (
              <div className="p-3 mb-4 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive">{tenantError}</div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 mb-0">
              <div className="flex-1 mb-4">
                <label htmlFor="tenantFirstName" className={labelClass}>First Name</label>
                <input id="tenantFirstName" type="text" value={tenantForm.firstName}
                  onChange={(e) => handleTenantFormChange('firstName', e.target.value)} className={inputClass} data-testid="tenant-first-name-input" />
              </div>
              <div className="flex-1 mb-4">
                <label htmlFor="tenantLastName" className={labelClass}>Last Name</label>
                <input id="tenantLastName" type="text" value={tenantForm.lastName}
                  onChange={(e) => handleTenantFormChange('lastName', e.target.value)} className={inputClass} data-testid="tenant-last-name-input" />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="tenantEmail" className={labelClass}>Email *</label>
              <input id="tenantEmail" type="email" value={tenantForm.email}
                onChange={(e) => handleTenantFormChange('email', e.target.value)} className={inputClass} required data-testid="tenant-email-input" />
            </div>
            {editingTenantId && (
              <CounterpartyAddressesPanel
                owner={{
                  kind: 'tenant',
                  id: editingTenantId,
                  primaryEmail: tenantForm.email.trim() || null,
                  name: `${tenantForm.firstName} ${tenantForm.lastName}`.trim() || 'this tenant',
                }}
                timezone={deriveTimezone(unit.state)}
                onPrimaryChanged={(newPrimary) => {
                  handleTenantFormChange('email', newPrimary);
                  void fetchData();
                }}
              />
            )}
            <div className="mb-4">
              <label htmlFor="tenantPhone" className={labelClass}>Phone</label>
              <input id="tenantPhone" type="tel" value={tenantForm.phone}
                onChange={(e) => handleTenantFormChange('phone', e.target.value)} className={inputClass} data-testid="tenant-phone-input" />
            </div>
            <div className="flex justify-end gap-4 mt-2">
              <Button type="button" variant="outline" onClick={() => setIsTenantModalOpen(false)} disabled={isSavingTenant}>Cancel</Button>
              <Button type="submit" disabled={isSavingTenant || !tenantForm.email.trim()} data-testid="save-tenant-button">
                {isSavingTenant ? 'Saving...' : editingTenantId ? 'Save Changes' : 'Add Tenant'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirm Dialogs */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={isSingleFamily ? 'Delete Property' : 'Delete Unit'}
        message={
          isSingleFamily
            ? `Are you sure you want to delete "${unit.property?.name}"? This will delete the property and all its data. This action cannot be undone.`
            : `Are you sure you want to delete ${formatUnitLabel(unit.unitNumber) ?? 'this unit'}? This action cannot be undone.`
        }
        confirmText={isSingleFamily ? 'Delete Property' : 'Delete Unit'}
        isDestructive
        isLoading={isDeleting}
      />
      <ConfirmDialog
        isOpen={deletingTenantId !== null}
        onClose={() => setDeletingTenantId(null)}
        onConfirm={handleDeleteTenant}
        title="Remove Tenant"
        message="Are you sure you want to remove this tenant? This will archive the tenant record and revoke any pending invites. This action cannot be undone."
        confirmText="Remove Tenant"
        isDestructive
        isLoading={isDeletingTenant}
      />

      {/* Invite Link Modal */}
      <Modal isOpen={viewingInviteTenantId !== null} onClose={() => setViewingInviteTenantId(null)} title="Tenant Invite Link">
        {viewingInviteTenantId && unit?.tenants && (() => {
          const tenant = unit.tenants.find(t => t.id === viewingInviteTenantId);
          const pendingInvite = tenant ? getPendingInvite(tenant) : null;

          if (!pendingInvite) {
            return <p className="text-sm text-destructive">No pending invite found for this tenant.</p>;
          }

          const inviteLink = getInviteLink(pendingInvite.token);
          return (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">Share this link with the tenant to complete their registration:</p>
              <div className="p-3 bg-muted border border-border rounded-md break-all">
                <code className="text-xs font-mono text-foreground" data-testid="invite-link">{inviteLink}</code>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" onClick={() => { navigator.clipboard.writeText(inviteLink); }} data-testid="copy-invite-link-button">
                  Copy Link
                </Button>
                <Button variant="outline" size="sm" onClick={() => setViewingInviteTenantId(null)}>Close</Button>
              </div>
              <div className="flex flex-col gap-1 p-3 bg-muted/50 rounded-md text-xs text-muted-foreground">
                <p><strong>Token:</strong> {pendingInvite.token}</p>
                <p><strong>Expires:</strong> {formatDateTime(pendingInvite.expiresAt)}</p>
                <p><strong>Status:</strong> {pendingInvite.status}</p>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Scheduled Lease Guard Dialog */}
      {isScheduledGuardOpen && (() => {
        const scheduledLease = leases.find(l => l.state === 'scheduled');
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
            onClick={() => setIsScheduledGuardOpen(false)}
            data-testid="scheduled-lease-guard-dialog"
          >
            <div
              className="bg-card rounded-lg w-[90%] max-w-md shadow-2xl p-6 flex flex-col gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-foreground m-0">Scheduled Lease Exists</h3>
              <p className="text-sm text-muted-foreground m-0">
                This unit already has a scheduled lease. Edit the existing lease or archive it first.
              </p>
              <div className="flex justify-end gap-3 mt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsScheduledGuardOpen(false)}
                  data-testid="scheduled-lease-guard-close"
                >
                  Close
                </Button>
                {scheduledLease && (
                  <Button
                    onClick={() => {
                      setIsScheduledGuardOpen(false);
                      navigate(`/leases/${scheduledLease.id}`);
                    }}
                    data-testid="scheduled-lease-guard-view"
                  >
                    View Scheduled Lease
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Lease Creation Choice Modal */}
      <Modal isOpen={isLeaseChoiceOpen} onClose={() => setIsLeaseChoiceOpen(false)} title="Create Lease">
        <p className="text-sm text-muted-foreground mb-6">How would you like to create this lease?</p>
        <div className="flex flex-col gap-4">
          {leaseDraft && (
            <button
              type="button"
              onClick={() => { setIsLeaseChoiceOpen(false); openCreateLeaseWizard(); }}
              className="flex flex-col items-start gap-2 p-5 border-2 border-warning/60 bg-warning/5 rounded-lg cursor-pointer hover:border-warning hover:bg-warning/10 transition-colors text-left min-h-[44px]"
              data-testid="lease-choice-resume-draft"
            >
              <span className="text-base font-semibold text-foreground">Resume Draft</span>
              <span className="text-sm text-muted-foreground">
                Continue where you left off — step {(leaseDraft.variables?.wizardCurrentStep as number | undefined) ?? 1} of 9
              </span>
            </button>
          )}
          {/* Hide wizard option if active lease was created via wizard (re-generating doesn't make sense) */}
          {activeLeases.some(l => l.source === 'wizard') ? (
            <button
              type="button"
              onClick={openManualLeaseForm}
              className="flex flex-col items-start gap-2 p-5 border-2 border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors text-left min-h-[44px]"
              data-testid="lease-choice-manual"
            >
              <span className="text-base font-semibold text-foreground">Bring Your Own</span>
              <span className="text-sm text-muted-foreground">Register a lease that was created outside of Resida</span>
            </button>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => { setIsLeaseChoiceOpen(false); openCreateLeaseWizard(); }}
                className="flex flex-col items-start gap-2 p-5 border-2 border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors text-left min-h-[44px]"
                data-testid="lease-choice-wizard"
              >
                <span className="text-base font-semibold text-foreground">Generate with Wizard</span>
                <span className="text-sm text-muted-foreground">Create a jurisdiction-aware lease with e-signing</span>
              </button>
              <button
                type="button"
                onClick={openManualLeaseForm}
                className="flex flex-col items-start gap-2 p-5 border-2 border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors text-left min-h-[44px]"
                data-testid="lease-choice-manual"
              >
                <span className="text-base font-semibold text-foreground">Bring Your Own</span>
                <span className="text-sm text-muted-foreground">Register a lease that was created outside of Resida</span>
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Lease Form Modal */}
      <Modal isOpen={isLeaseFormOpen} onClose={() => setIsLeaseFormOpen(false)} title={editingLeaseId ? 'Edit Lease' : 'Register Existing Lease'} width="large">
        <LeaseForm
          mode={editingLeaseId ? 'edit' : 'create'}
          lease={editingLease ? { ...editingLease, unit: { id: unitId!, unitNumber: unit?.unitNumber ?? '' } } : undefined}
          unitId={unitId!}
          availableTenants={unit?.tenants || []}
          initialValues={editingLeaseId ? undefined : leaseFormInitial}
          onSubmit={handleSaveLease}
          onCancel={() => setIsLeaseFormOpen(false)}
          isSubmitting={isSavingLease}
          warnings={leaseWarnings}
          submitError={leaseSubmitError}
          onClearError={() => setLeaseSubmitError(null)}
        />
      </Modal>

      {/* Archive Lease Modal */}
      {archivingLease && (
        <ArchiveLeaseModal
          lease={archivingLease}
          onConfirm={handleArchiveLease}
          onCancel={() => { setArchivingLease(null); setArchiveError(null); }}
          isSubmitting={isArchivingLease}
          error={archiveError}
          onClearError={() => setArchiveError(null)}
        />
      )}

    </div>
  );
}

export default UnitDetailPage;
